import React from 'react';
import { Send } from 'lucide-react';
import { useCountdownTimer } from '@/hooks/useCountdownTimer';
import { useVerificationAttempts } from '@/hooks/useVerificationAttempts';

const MAX_ATTEMPTS = 2;

interface VerificationButtonProps {
  onSendCode: () => Promise<void>;
  isLoading?: boolean;
}

export default function VerificationButton({ 
  onSendCode, 
  isLoading = false 
}: VerificationButtonProps) {
  const { timeLeft, isRunning, startTimer } = useCountdownTimer({
    duration: 120, // 2 minutes
    onComplete: () => {
      // Timer completed
    }
  });

  const { 
    attempts, 
    hasReachedLimit, 
    incrementAttempts 
  } = useVerificationAttempts();

  const handleClick = async () => {
    try {
      incrementAttempts();
      startTimer();
      await onSendCode();
    } catch (error) {
      console.error('Error sending code:', error);
    }
  };

  const isDisabled = isLoading || isRunning || hasReachedLimit;

  if (hasReachedLimit) {
    return (
      <div className="space-y-5">
        <div className="bg-amber-50/80 backdrop-blur-sm border border-amber-200 rounded-xl p-6 shadow-md">
          <h3 className="text-amber-800 font-semibold mb-3 text-center">
            Here are some reasons you might not be seeing your unique code:
          </h3>
          <ol className="list-decimal list-inside space-y-3 text-amber-700">
            <li>You might need to check in spam, promotions or other subfolders in your inbox</li>
            <li>Your email provider might be delayed</li>
            <li>Our validation system is having issues</li>
          </ol>
          <p className="mt-5 text-amber-800 text-center">
            In any case, we're eager to help you ASAP. Please send an email to{' '}
            <a 
              href="mailto:voice@insightalks.com" 
              className="text-primary hover:text-primary-hover underline font-medium"
            >
              voice@insightalks.com
            </a>
            {' '}and we'll promptly provide the code to you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className="w-full bg-primary text-dark px-6 py-3 rounded-lg hover:bg-primary-hover transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-dark border-t-transparent mr-2"></div>
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send confirmation code
          </>
        )}
      </button>
      
      {isRunning && timeLeft > 0 && (
        <p className="text-sm text-gray-600 text-center mt-2">
          You can request another code in {Math.floor(timeLeft / 60)}:
          {(timeLeft % 60).toString().padStart(2, '0')}
        </p>
      )}
      
      {attempts > 0 && !hasReachedLimit && (
        <p className="text-sm text-gray-600 text-center mt-2">
          {MAX_ATTEMPTS - attempts + 1} attempts remaining
        </p>
      )}
    </div>
  );
}